import { useEffect, useState } from 'react';
import { CATALOG, TEMPLATES, sheetSize, tentFlat, type CatalogItem, type Product } from '@cal/engine';
import { get, post, errorMessage } from '../api';
import { navigate } from '../router';
import { Check, Field, Modal, Select } from '../ui/controls';
import { toast } from '../ui/toast';
import { useLibrary } from '../library';
import { TemplateIcon } from '../editor/DesignStage';
import { HolidayOffer, createBlocker } from '../ui/HolidayOffer';
import { CustomBuilder } from './CustomBuilder';

interface Customer { id: string; name: string }

/** Name and year, kept when switching between the ways to start. */
export interface Shared { name: string; year: number; setName: (v: string) => void; setYear: (v: number) => void }

type Start = 'catalogue' | 'saved' | 'blank';
const STARTS: { value: Start; label: string; detail: string }[] = [
  { value: 'catalogue', label: 'Product catalogue', detail: 'Print Station products with ready-made templates' },
  { value: 'saved', label: 'My templates', detail: 'Designs saved from earlier projects' },
  { value: 'blank', label: 'Build your own', detail: 'Any size, calendar type and layout grid' },
];

/**
 * One place to start a calendar: from the product catalogue, from one of my
 * templates, or built from scratch on a layout grid.
 */
export function NewCalendar({ onClose }: { onClose: () => void }) {
  const [start, setStart] = useState<Start>('catalogue');
  const [name, setName] = useState('');
  const [year, setYear] = useState(new Date().getFullYear() + 1);
  const shared: Shared = { name, year, setName, setYear };
  const chooser = (
    <div className="field" role="group" aria-label="Start from">
      <span className="lbl">Start from</span>
      <div className="start-grid">
        {STARTS.map((s) => (
          <button key={s.value} type="button" className="start-card" aria-pressed={start === s.value} onClick={() => setStart(s.value)}>
            <strong>{s.label}</strong>
            <span className="small faint">{s.detail}</span>
          </button>
        ))}
      </div>
    </div>
  );
  return start === 'catalogue'
    ? <NewProject onClose={onClose} start={chooser} shared={shared} />
    : <CustomBuilder key={start} onClose={onClose} start={chooser} shared={shared} mode={start} />;
}

/** Outline of a catalogue product at its true proportions (tents: the flat sheet with folds). */
function ProductShape({ item }: { item: CatalogItem }) {
  const p = { sheetPreset: 'A4', orientation: 'portrait', widthMm: 210, heightMm: 297, ...item.product } as Product;
  const { w, h } = item.product.carrier?.enabled ? { w: item.product.carrier.widthMm, h: item.product.carrier.heightMm } : sheetSize(p);
  const folds = p.productType === 'tent' && p.tent ? tentFlat({ ...p, widthMm: w, heightMm: h }).folds : [];
  const box = 56, s = box / Math.max(w, h);
  const sw = 1 / s;
  return (
    <svg width={box} height={box} viewBox={`${-(Math.max(w, h) - w) / 2} ${-(Math.max(w, h) - h) / 2} ${Math.max(w, h)} ${Math.max(w, h)}`} aria-hidden style={{ flex: 'none' }}>
      <rect x={0} y={0} width={w} height={h} fill="#fff" stroke="#8fa3b8" strokeWidth={sw * 1.2} />
      {p.productType === 'year' && Array.from({ length: 12 }, (_, i) => <rect key={i} x={w * (0.08 + (i % 4) * 0.22)} y={h * (0.2 + Math.floor(i / 4) * 0.25)} width={w * 0.18} height={h * 0.2} fill="#dbe7f5" />)}
      {p.productType === 'monthly' && <><rect x={w * 0.08} y={h * 0.08} width={w * 0.84} height={h * 0.42} fill="#f3e3cf" /><rect x={w * 0.08} y={h * 0.56} width={w * 0.84} height={h * 0.36} fill="#dbe7f5" /></>}
      {folds.map((f, i) => <line key={i} x1={f.x1} y1={f.y1} x2={f.x2} y2={f.y2} stroke="#2f7a4a" strokeWidth={sw} strokeDasharray={`${sw * 3} ${sw * 2}`} />)}
    </svg>
  );
}

export function NewProject({ onClose, start, shared }: { onClose: () => void; start: React.ReactNode; shared: Shared }) {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const sets = useLibrary((s) => s.eventSets);
  const loadSets = useLibrary((s) => s.loadEventSets);
  const [f, setF] = useState({ customerId: '', newCustomer: '', catalogId: 'wall-a4', templateId: 'photo-top', includeCover: false });
  const [setIds, setSetIds] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { get<{ customers: Customer[] }>('/api/customers').then((r) => setCustomers(r.customers)).catch(() => undefined); void loadSets(); }, [loadSets]);
  const item = CATALOG.find((c) => c.id === f.catalogId)!;
  const type = item.product.productType;
  const templates = TEMPLATES.filter((t) => t.product === type && t.audience !== 'custom');
  const yearSets = sets.filter((s) => s.year === shared.year || s.year === null);
  const chosen = setIds ?? yearSets.filter((s) => s.kind === 'public').map((s) => s.id);
  const groups = [...new Set(CATALOG.map((c) => c.group))];
  const pick = (id: string) => {
    const c = CATALOG.find((x) => x.id === id)!;
    setF({ ...f, catalogId: id, includeCover: c.product.includeCover ?? false, templateId: TEMPLATES.find((t) => t.id === f.templateId)?.product === c.product.productType ? f.templateId : c.templateId });
  };
  return (
    <Modal wide title="New calendar" onClose={onClose} footer={<>{createBlocker(shared) && <span className="small muted grow">{createBlocker(shared)}</span>}<button className="btn" onClick={onClose}>Cancel</button><button className="btn primary" disabled={busy || !!createBlocker(shared)} onClick={async () => {
      setBusy(true);
      try {
        let customerId: string | null = f.customerId || null;
        if (f.customerId === '__new') customerId = (await post<{ customer: Customer }>('/api/customers', { name: f.newCustomer })).customer.id;
        const r = await post<{ id: string }>('/api/projects', { name: shared.name, customerId, year: shared.year, templateId: f.templateId, product: { ...item.product, catalogId: item.id, ...(type === 'monthly' ? { includeCover: f.includeCover } : {}) }, eventSetIds: chosen.filter((id) => yearSets.some((s) => s.id === id)) });
        navigate(`/projects/${r.id}`);
      } catch (e) { toast(errorMessage(e), 'err'); setBusy(false); }
    }}>{busy ? 'Creating…' : 'Create and open'}</button></>}>
      {start}
      <div className="grid2">
        <Field label="Project name"><input className="input" value={shared.name} placeholder={`e.g. Coastal Plumbing magnets ${shared.year}`} onChange={(e) => shared.setName(e.target.value)} autoFocus /></Field>
        <Field label="Year"><input className="input" type="number" min={1900} max={2100} value={shared.year} onChange={(e) => { shared.setYear(+e.target.value); setSetIds(null); }} /></Field>
        <Select label="Customer" value={f.customerId} onChange={(v) => setF({ ...f, customerId: v })} options={[{ value: '', label: 'No customer' }, { value: '__new', label: '+ New customer…' }, ...customers.map((c) => ({ value: c.id, label: c.name }))]} />
        {f.customerId === '__new' && <Field label="New customer name"><input className="input" value={f.newCustomer} onChange={(e) => setF({ ...f, newCustomer: e.target.value })} /></Field>}
      </div>
      <Field label="Product">
        <div className="col">
          {groups.map((g) => (
            <div key={g} className="col" style={{ gap: 4 }}>
              <span className="small faint">{g}</span>
              <div className="tpl-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
                {CATALOG.filter((c) => c.group === g).map((c) => (
                  <button key={c.id} type="button" className="tpl-card product-card" title={c.description} aria-label={c.name} aria-pressed={f.catalogId === c.id} onClick={() => pick(c.id)}>
                    <ProductShape item={c} />
                    <span className="small" style={{ fontWeight: 600 }}>{c.name}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
          <span className="small muted">{item.description} You can change size, bleed and finishing later under Product.</span>
        </div>
      </Field>
      {type === 'monthly' && <Check label={item.product.duplex ? 'Include a cover and back cover (7 double-sided leaves)' : 'Include a cover page (13 sheets)'} checked={f.includeCover} onChange={(v) => setF({ ...f, includeCover: v })} />}
      <Field label="Template">
        <div className="tpl-grid">
          {templates.map((t) => (
            <button key={t.id} type="button" className="tpl-card" aria-pressed={f.templateId === t.id} onClick={() => setF({ ...f, templateId: t.id })}>
              <TemplateIcon id={t.id} /><span className="small" style={{ fontWeight: 600 }}>{t.name}</span>
              {t.audience !== 'general' && <span className="badge">{({ business: 'Business', personal: 'Personal', 'estate-agent': 'Estate agent' } as Record<string, string>)[t.audience]}</span>}
              <span className="small faint">{t.description}</span>
            </button>
          ))}
        </div>
      </Field>
      <Field label={`Holidays for ${shared.year}`}>
        <div className="col">
          {!yearSets.some((s) => s.kind === 'public') && <HolidayOffer year={shared.year} />}
          {yearSets.map((s) => (
            <label key={s.id} className="check">
              <input type="checkbox" checked={chosen.includes(s.id)} onChange={(e) => setSetIds(e.target.checked ? [...chosen, s.id] : chosen.filter((x) => x !== s.id))} />
              <span>{s.name} <span className={`badge ${s.verification === 'verified' ? 'ok' : 'warn'}`}>{s.verification}</span></span>
            </label>
          ))}
        </div>
      </Field>
    </Modal>
  );
}
